import type { ReactNode } from 'react';
import { Box, Flex, Heading, HStack, Image, Text } from '@chakra-ui/react';
import { useSearchParams } from 'react-router-dom';
import { DeckSelector } from '../components/deck/DeckSelector';
import { useReportMobileFilterCount } from '../components/layout/MobileFilterSheetContext';
import { RiftboundButton } from '../components/ui/button';
import { OptionTileGroup } from '../components/ui/option-tile';
import { RiftboundSelectMenu } from '../components/RiftboundSelectMenu';
import { BEST_OF_CHOICES, MATCH_FORMAT_OPTIONS } from '../constants/matchFormats';
import { SETS, metaPeriodFilterOptions, metaPeriodInclusiveEndDate, setLogoUrl, type ConstructedMetaPeriodOption } from '../constants';
import { useUserDecks } from '../hooks/useDeck';
import type { Deck } from '../types/deck';
import type { MatchHistoryFilters } from '../types/match';
import { matchHistoryFilterCount, parseMatchHistoryFilters } from '../utils/matchTracker';

const BEST_OF_FILTER_OPTIONS = [
  { value: 'all', label: 'All lengths', helper: 'Any match length' },
  ...BEST_OF_CHOICES.map((option) => ({
    value: String(option.value),
    label: option.label,
    helper: option.helper,
  })),
];

const RESULT_FILTER_OPTIONS = [
  { value: 'all', label: 'All results', helper: 'Wins, losses, and ties' },
  { value: 'wins', label: 'Wins', helper: 'Matches you won' },
  { value: 'losses', label: 'Losses', helper: 'Matches you lost' },
  { value: 'draws', label: 'Ties', helper: 'Matches that ended in a draw' },
] as const;

const RANGE_FILTER_OPTIONS = [
  { value: 'all', label: 'All time', helper: 'Full history' },
  { value: '7d', label: 'Last 7 days', helper: 'Recent week' },
  { value: '30d', label: 'Last 30 days', helper: 'Recent month' },
] as const;

const META_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

function formatUtcDay(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);
  if (!year || !month || !day) return iso;
  return META_DATE_FORMATTER.format(new Date(Date.UTC(year, month - 1, day)));
}

function setDisplayName(code: string): string {
  return SETS.find((set) => set.code.toUpperCase() === code.toUpperCase())?.name ?? code;
}

function metaPeriodHelper(period: ConstructedMetaPeriodOption): string {
  const name = setDisplayName(period.code);
  const start = formatUtcDay(period.startDate);
  if (period.isCurrent) return `${name} · ${start} – present`;
  const inclusiveEnd = metaPeriodInclusiveEndDate(period);
  return inclusiveEnd ? `${name} · ${start} – ${formatUtcDay(inclusiveEnd)}` : `${name} · since ${start}`;
}

function SetLogo({ code, size = '22px' }: { code?: string; size?: string }) {
  const src = code ? setLogoUrl(code) : undefined;
  if (!src) return null;
  return <Image src={src} alt="" boxSize={size} objectFit="contain" flexShrink={0} />;
}

function MetaSelectOptionLabel({
  code,
  helper,
}: {
  code?: string;
  helper: string;
}) {
  return (
    <HStack gap={3} align="center" minW={0} py={0.5}>
      {code ? <SetLogo code={code} /> : null}
      <Box minW={0}>
        <Text fontWeight="black" lineHeight="1.1">
          {code ?? 'All metas'}
        </Text>
        <Text fontSize="2xs" color="textMuted" mt={0.5} lineClamp={2}>
          {helper}
        </Text>
      </Box>
    </HStack>
  );
}

interface MatchHistoryFiltersPanelProps {
  filters: MatchHistoryFilters;
  decks: Deck[];
  decksLoading: boolean;
  activeFilterCount?: number;
  layout?: 'inline' | 'sidebar';
  onUpdateParam: (key: string, value: string) => void;
  onClearFilters?: () => void;
}

function FilterSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box w="100%" minW={0}>
      <Heading as="h3" size="sm" mb={2}>
        {title}
      </Heading>
      {children}
    </Box>
  );
}

export function MatchHistoryFiltersPanel({
  filters,
  decks,
  decksLoading,
  activeFilterCount = 0,
  layout = 'inline',
  onUpdateParam,
  onClearFilters,
}: MatchHistoryFiltersPanelProps) {
  const isSidebar = layout === 'sidebar';
  const metaOptions = metaPeriodFilterOptions();
  const metaValue = filters.meta ?? 'all';
  const metaSelectOptions = [
    {
      value: 'all',
      triggerLabel: 'All metas',
      label: <MetaSelectOptionLabel helper="Any constructed period" />,
    },
    ...metaOptions.map((period) => ({
      value: period.code,
      triggerLabel: period.code,
      label: <MetaSelectOptionLabel code={period.code} helper={metaPeriodHelper(period)} />,
    })),
  ];

  return (
    <Flex gap={5} direction={isSidebar ? 'column' : { base: 'column', lg: 'row' }} align="flex-start">
      <FilterSection title="Meta">
        <RiftboundSelectMenu
          ariaLabel="Meta"
          value={metaValue}
          options={metaSelectOptions}
          onValueChange={(value) => onUpdateParam('meta', value)}
          placement="bottom-start"
          menuWidth="280px"
          triggerProps={{
            icon: <SetLogo code={metaValue === 'all' ? undefined : metaValue} size="18px" />,
          }}
        />
      </FilterSection>

      <FilterSection title="Deck">
        <Box flex={isSidebar ? '0 0 auto' : { base: '1 1 auto', lg: '1 1 320px' }} minW={0} w="100%">
          <DeckSelector
            decks={decks}
            loading={decksLoading}
            value={filters.deckId}
            onChange={(deckId) => onUpdateParam('deck', deckId ?? 'all')}
            allowClear
            clearLabel="All decks"
            hideScrollbar
          />
        </Box>
      </FilterSection>

      <FilterSection title="Match length">
        <OptionTileGroup
          options={BEST_OF_FILTER_OPTIONS}
          value={(filters.bestOf ?? 'all').toString()}
          onChange={(value) => onUpdateParam('bestOf', value)}
          columns={isSidebar ? 2 : { base: 2, sm: 4 }}
        />
      </FilterSection>

      <FilterSection title="Format">
        <OptionTileGroup
          options={[
            { value: 'all', label: 'All formats', helper: 'Do not limit by event type' },
            ...MATCH_FORMAT_OPTIONS.map((option) => ({
              value: option.value,
              label: option.label,
              helper: `Best of ${option.recommendedBestOf}, to ${option.recommendedTargetScore}`,
            })),
          ]}
          value={filters.format ?? 'all'}
          onChange={(value) => onUpdateParam('format', value)}
          columns={isSidebar ? 1 : { base: 1, sm: 2 }}
        />
      </FilterSection>

      <FilterSection title="Date played">
        <OptionTileGroup
          options={RANGE_FILTER_OPTIONS}
          value={filters.range ?? 'all'}
          onChange={(value) => onUpdateParam('range', value)}
          columns={isSidebar ? 1 : { base: 1, sm: 3 }}
        />
      </FilterSection>

      <FilterSection title="Result">
        <OptionTileGroup
          options={RESULT_FILTER_OPTIONS}
          value={filters.result ?? 'all'}
          onChange={(value) => onUpdateParam('result', value)}
          columns={isSidebar ? 1 : { base: 1, sm: 3 }}
        />
      </FilterSection>

      {activeFilterCount > 0 && onClearFilters ? (
        <RiftboundButton size="sm" treatment="ghost" onClick={onClearFilters} w={isSidebar ? '100%' : 'auto'}>
          Clear filters
        </RiftboundButton>
      ) : null}
    </Flex>
  );
}

export function MatchHistorySidebarBound() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { decks, loading: decksLoading } = useUserDecks();
  const filters = parseMatchHistoryFilters(searchParams);
  const activeFilterCount = matchHistoryFilterCount(filters);

  useReportMobileFilterCount(activeFilterCount);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === 'all') next.delete(key);
    else next.set(key, value);
    if (key === 'deck') next.delete('legend');
    if (key === 'legend') next.delete('deck');
    setSearchParams(next);
  };

  return (
    <Box bg="bgElevated" borderWidth="hairline" borderColor="border" borderRadius="2xl" p={4}>
      <MatchHistoryFiltersPanel
        filters={filters}
        decks={decks}
        decksLoading={decksLoading}
        activeFilterCount={activeFilterCount}
        layout="sidebar"
        onUpdateParam={updateParam}
        onClearFilters={() => setSearchParams(new URLSearchParams())}
      />
    </Box>
  );
}
